import * as React from 'react';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import Slider from '@material-ui/core/Slider';
import TextField from '@material-ui/core/TextField';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Grid from '@material-ui/core/Grid';
import { debounce } from 'lodash';
import { Parameter } from 'shapediver-types';
import { isNumber } from 'lodash';
import './number-input.css';

type ParameterDefinition = Parameter.ParameterDefinition;

const useStyles = makeStyles(() => ({
  input: {
    width: 80,
    marginLeft: 10,
    borderRadius: 4,

    '& > .MuiFilledInput-input': {
      padding: 0,
      '-moz-appearance': 'textfield',

      '&::-webkit-outer-spin-button,&::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
    },
  },
}));

type StepFn = (definition: ParameterDefinition) => number;

type Props = {
  initialValue: number;
  definition: ParameterDefinition;
  onChange: (newValue: number) => void;
};

function RangeInputFactory(stepFn: StepFn) {
  return function NumberComponentBase(props: Props): React.ReactElement {
    const { definition, initialValue, onChange } = props;
    const { id } = definition;

    const classes = useStyles();
    const [value, setValue] = useState(initialValue);
    const [textValue, setTextValue] = useState(String(initialValue));

    const step = stepFn(definition);
    const min = isNumber(definition.min) ? Number(definition.min) : undefined;
    const max = isNumber(definition.max) ? Number(definition.max) : undefined;

    const onChangeDebounced = useMemo(() => {
      return debounce(onChange, 100);
    }, [onChange]);

    const onSliderChange = useCallback(
      (event: ChangeEvent<unknown>, newValue: number | number[]): void => {
        newValue = Array.isArray(newValue) ? newValue[0] : newValue;

        setValue(newValue);
        setTextValue(newValue.toString());
        onChangeDebounced(newValue);
      },
      [setValue, onChangeDebounced]
    );

    const onInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
      const stringValue = event.target.value;
      setTextValue(stringValue);

      if (stringValue !== '') {
        let numberValue = Number(stringValue);
        if (min !== undefined && numberValue < min) numberValue = min;
        if (max !== undefined && numberValue > max) numberValue = max;

        setValue(numberValue);
        onChangeDebounced(numberValue);
      }
    };

    const onInputBlur = (): void => {
      if (textValue !== value.toString()) {
        setTextValue(value.toString());
      }
    };

    const inputProps = useMemo(
      () => ({
        id,
        step: step,
        min: min,
        max: max,
        type: 'number',
      }),
      [id, min, max, step]
    );

    return (
      <Grid container spacing={0} alignItems="center">
        <Grid item xs>
          <Slider
            value={value}
            onChange={onSliderChange}
            min={min}
            max={max}
            step={step}
            // aria-labelledby={labelId}
          />
        </Grid>
        <Grid item>
          <TextField
            size={'small'}
            variant={'outlined'}
            className={classes.input}
            value={textValue}
            margin="none"
            name={id}
            inputProps={inputProps}
            onChange={onInputChange}
            onBlur={onInputBlur}
          />
        </Grid>
      </Grid>
    );
  };
}

export const IntInput = RangeInputFactory(() => 1);
export const EvenOddInput = RangeInputFactory(() => 2);
export const FloatInput = RangeInputFactory(
  (d) => 1 / 10 ** (d.decimalplaces || 1)
);
